import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  DEFAULT_TRIP_PAGE_SIZE,
  TRIP_SORT_FIELDS,
  type SortDirection,
  type TripDto,
  type TripListPage,
  type TripSortField,
  type TripStatus,
} from "@tc/shared";
import {
  Icon,
  Pagination,
  Segmented,
  type SegmentedOption,
  SortHeader,
  StatusBadge,
  TRIP_STATUS_BADGE,
} from "@/components/ui";
import { useFormatters } from "@/lib/i18n/format";
import { tripsApi } from "./api";
import { STATUS_KEY } from "./status";
import { DatesPassedTag } from "./DatesPassedTag";

/** List filter: everything, all live trips, or a single status. */
type TripFilter = "all" | "active" | TripStatus;

const FILTER_OPTIONS: { value: TripFilter; labelKey: string }[] = [
  { value: "all", labelKey: "trips.filterAll" },
  { value: "active", labelKey: "trips.filterActive" },
  { value: "Enquiry", labelKey: STATUS_KEY.Enquiry },
  { value: "ProposalSent", labelKey: STATUS_KEY.ProposalSent },
  { value: "Accepted", labelKey: STATUS_KEY.Accepted },
  { value: "Expired", labelKey: STATUS_KEY.Expired },
  { value: "Completed", labelKey: STATUS_KEY.Completed },
  { value: "Postponed", labelKey: STATUS_KEY.Postponed },
];

/** Sort column from the URL; absent/invalid falls back to travel dates (TRAV-71: default = date ascending). */
function readSort(value: string | null): TripSortField {
  return value && (TRIP_SORT_FIELDS as readonly string[]).includes(value)
    ? (value as TripSortField)
    : "dates";
}

/**
 * Trips list + lifecycle (TW-3437; server-side pagination + sorting added later). Reads the real
 * club-scoped `/api/trips` page (RLS shows a club user only its own club's trips, back-office all
 * of them). Page, sort column/direction and the status filter all live in the URL (`?page&sort&
 * dir&status`), so paging is shareable/back-navigable. Sorting + filtering + slicing happen on the
 * server; the client just renders one page. A `trip.write` holder can advance the status.
 */
export function TripsPage() {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const page = Math.max(1, Number(params.get("page")) || 1);
  const sort = readSort(params.get("sort"));
  const dir: SortDirection = params.get("dir") === "desc" ? "desc" : "asc";
  const filter: TripFilter = (params.get("status") as TripFilter) || "active";

  const [data, setData] = useState<TripListPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      setData(
        await tripsApi.listPage({
          page,
          pageSize: DEFAULT_TRIP_PAGE_SIZE,
          sort,
          dir,
          status: filter === "all" ? undefined : filter,
        }),
      );
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [page, sort, dir, filter]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /** Patch the URL search params; clearing `page` (back to 1) whenever the result set changes. */
  function patch(mutate: (next: URLSearchParams) => void, resetPage = true) {
    const next = new URLSearchParams(params);
    mutate(next);
    if (resetPage) next.delete("page");
    setParams(next);
  }

  function onSort(field: string) {
    patch((next) => {
      if (sort === field) {
        next.set("sort", field);
        next.set("dir", dir === "asc" ? "desc" : "asc");
      } else {
        next.set("sort", field);
        next.set("dir", "asc");
      }
    });
  }

  function onFilter(value: TripFilter) {
    patch((next) => (value === "active" ? next.delete("status") : next.set("status", value)));
  }

  function onPage(p: number) {
    patch((next) => (p <= 1 ? next.delete("page") : next.set("page", String(p))), false);
  }

  const filterOptions: SegmentedOption<TripFilter>[] = FILTER_OPTIONS.map((o) => ({
    value: o.value,
    label: t(o.labelKey),
  }));

  function dates(trip: TripDto): string {
    if (!trip.travelStartDate && !trip.travelEndDate) return "—";
    const start = trip.travelStartDate ? fmt.date(trip.travelStartDate) : "—";
    const end = trip.travelEndDate ? fmt.date(trip.travelEndDate) : "—";
    return `${start} → ${end}`;
  }

  const trips = data?.items ?? [];
  const total = data?.total ?? 0;
  const pageSize = data?.pageSize ?? DEFAULT_TRIP_PAGE_SIZE;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <div className="page">
      <div className="page-head">
        <h1>{t("trips.title")}</h1>
        <div className="sub">{t("trips.sub")}</div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <Segmented options={filterOptions} value={filter} onChange={onFilter} size="sm" />
      </div>

      {loading ? (
        <div className="empty">{t("common.loading")}</div>
      ) : error ? (
        <div className="empty">{t("trips.loadError")}</div>
      ) : total === 0 ? (
        <div className="empty">
          <div className="empty-ic">
            <Icon name="trips" size={24} />
          </div>
          {t("trips.empty")}
        </div>
      ) : (
        <div className="card card-pad">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <SortHeader label={t("trips.colReference")} field="reference" active={sort === "reference"} dir={dir} onSort={onSort} />
                  <SortHeader label={t("trips.colTrip")} field="trip" active={sort === "trip"} dir={dir} onSort={onSort} />
                  <SortHeader label={t("trips.colDest")} field="destination" active={sort === "destination"} dir={dir} onSort={onSort} />
                  <SortHeader label={t("trips.colDates")} field="dates" active={sort === "dates"} dir={dir} onSort={onSort} />
                  <th>{t("trips.colPax")}</th>
                  <th>{t("trips.colStatus")}</th>
                </tr>
              </thead>
              <tbody>
                {trips.map((trip) => (
                  <tr key={trip.id} onClick={() => navigate(`/trips/${trip.id}`)} style={{ cursor: "pointer" }}>
                    <td>
                      <b>{trip.reference}</b>
                    </td>
                    <td className="t-trip">
                      <b>{trip.matchOpponent ?? trip.sport ?? "—"}</b>
                      {trip.competition && <small>{trip.competition}</small>}
                    </td>
                    <td>
                      {[trip.destinationCity, trip.destinationCountry ? fmt.country(trip.destinationCountry) : ""].filter(Boolean).join(", ") || "—"}
                    </td>
                    <td>
                      {dates(trip)}
                      <DatesPassedTag trip={trip} />
                    </td>
                    <td>{trip.paxCount ?? "—"}</td>
                    <td>
                      <StatusBadge status={TRIP_STATUS_BADGE[trip.status]} label={t(STATUS_KEY[trip.status])} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            page={page}
            pageCount={pageCount}
            onPage={onPage}
            prevLabel={t("trips.pager.prev")}
            nextLabel={t("trips.pager.next")}
            summary={t("trips.pager.summary", { from, to, total })}
          />
        </div>
      )}
    </div>
  );
}
